import { z } from 'zod';
import { badgeSchema } from './badges.js';
import { utcOffsetMinutesSchema, idSchema, slugSchema } from './common.js';
import { homeRecommendationSchema, lessonScoreSchema } from './home.js';
/**
 * What a learner sends and what they get back. Tables are docs/specs/012-data-model.md
 * §10; the grading rules the server applies are §7's table read from the answer side.
 *
 * Not in the package barrel — import `@platform/schema/progress`. Same reason as
 * `content`: the barrel is on the mobile boot path and Metro does not tree-shake.
 */
export const attemptIdSchema = idSchema
    .describe("One learner's run at one lesson.")
    .brand();
/**
 * One answer, shaped by the mechanic that produced it.
 *
 * Discriminated on `kind` so the server never has to guess which shape it was handed,
 * and `assertNever` makes a twelfth mechanic a compile error in the grader rather than
 * a submission that silently scores zero.
 */
export const sceneAnswerSchema = z
    .discriminatedUnion('kind', [
    z.object({
        kind: z.literal('CHOICE'),
        selected: z
            .array(idSchema)
            .min(1)
            .max(8)
            .describe('The option ids the learner picked.'),
    }),
    z.object({
        kind: z.literal('RANK'),
        order: z
            .array(idSchema)
            .min(2)
            .max(8)
            .describe('Item ids in the order the learner placed them, first to last.'),
    }),
    z.object({
        kind: z.literal('BUCKET'),
        placements: z
            .array(z.object({ itemId: idSchema, bucketId: idSchema }))
            .min(1)
            .max(12)
            .describe('Where each item was dropped.'),
    }),
    z.object({
        kind: z.literal('SLIDER'),
        value: z.number().describe('Where the learner left the handle.'),
    }),
    z.object({
        kind: z.literal('ALLOCATE'),
        allocations: z
            .array(z.object({ itemId: idSchema, amount: z.number().min(0) }))
            .min(1)
            .max(8)
            .describe('How the learner divided the pool.'),
    }),
    z.object({
        kind: z.literal('SPEAK'),
        text: z
            .string()
            .min(1)
            .max(4000)
            .describe('What the learner said, transcribed.'),
    }),
])
    .describe('A learner’s answer to one interactive scene.');
export const submitAnswerSchema = z
    .object({
    sceneId: idSchema,
    elapsedMs: z
        .number()
        .int()
        .min(0)
        .max(3_600_000)
        .describe('How long the learner spent, for the performance record.'),
    answer: sceneAnswerSchema,
})
    .describe('POST /attempts/:id/scenes');
/**
 * One category of an ALLOCATE answer, read against the range it was graded on.
 *
 * The range travels with the verdict because the learner never receives the key. Without
 * it a client can say the answer was wrong and not which category or which way, and
 * "wrong" is the one thing a budget split is rarely allowed to be — 005 §3.
 *
 * `example` is the author's own split, carried as one defensible answer rather than as
 * the answer: a learner outside it but inside the range has not made a mistake.
 */
export const allocationOutcomeSchema = z
    .object({
    itemId: idSchema,
    amount: z.number().describe('What the learner put here.'),
    min: z.number().describe('Bottom of the defensible range, inclusive.'),
    max: z.number().describe('Top of it, inclusive.'),
    example: z.number().describe('The author’s own split for this category.'),
})
    .describe('One category of an ALLOCATE answer, against its range.');
/**
 * One item of a BUCKET answer, beside the bucket it belongs in.
 *
 * Carried for the reason `allocationOutcomeSchema` is: a score of 50% says half the
 * items are misplaced and not which half, so the learner cannot learn from it.
 */
export const placementOutcomeSchema = z
    .object({
    itemId: idSchema,
    bucketId: idSchema
        .nullable()
        .describe('Where the learner put it. Null when it was never placed.'),
    correctBucketId: idSchema.describe('Where it belongs.'),
})
    .describe('One item of a BUCKET answer, against its bucket.');
/**
 * One item of a RANK answer, beside the place it belongs in — so a partial score can
 * show the order the learner locked next to the strongest one.
 */
export const rankingOutcomeSchema = z
    .object({
    itemId: idSchema,
    position: z
        .number()
        .int()
        .min(1)
        .nullable()
        .describe('Where the learner put it, from 1. Null when it was left out.'),
    correctPosition: z.number().int().min(1).describe('Where it belongs.'),
})
    .describe('One item of a RANK answer, against its place.');
/**
 * A SLIDER answer beside the band it was graded on, so a miss can say how far off it
 * was — a 0% on a number says nothing a learner can act on.
 */
export const estimateOutcomeSchema = z
    .object({
    value: z.number().describe('Where the learner set it.'),
    min: z.number().describe('Bottom of the band that scores, inclusive.'),
    max: z.number().describe('Top of it, inclusive.'),
})
    .describe('A SLIDER answer, against its band.');
/**
 * The verdict, returned with the explanation rather than before it.
 *
 * 005 §3 names explain-then-confirm as the failure mode that makes a quiz app, so the
 * explanation arriving only here is what makes "do first, explain second" impossible to
 * build backwards — a client cannot show it early because it has not been sent.
 */
export const verdictSchema = z
    .object({
    correct: z.boolean().describe('Whether the answer was fully right.'),
    scorePct: z
        .number()
        .int()
        .min(0)
        .max(100)
        .describe('Partial credit, where the mechanic supports it. RANK does.'),
    explanation: z
        .string()
        .describe('Why. Empty only for a scene with no question behind it.'),
    allocation: z
        .array(allocationOutcomeSchema)
        .max(8)
        .optional()
        .describe('ALLOCATE only: each category read against its range.'),
    placement: z
        .array(placementOutcomeSchema)
        .max(12)
        .optional()
        .describe('BUCKET only: each item beside the bucket it belongs in.'),
    ranking: z
        .array(rankingOutcomeSchema)
        .max(8)
        .optional()
        .describe('RANK only: each item beside the place it belongs in.'),
    estimate: estimateOutcomeSchema
        .optional()
        .describe('SLIDER only: the number beside its band.'),
    correctOptionIds: z
        .array(idSchema)
        .max(8)
        .optional()
        .describe('CHOICE only: the options that make the right answer, so a miss can mark them.'),
    scored: z
        .literal(false)
        .optional()
        .describe('SPEAK only, until a grader exists: nothing scored this answer, so correct and scorePct mean nothing and the lesson score leaves it out.'),
})
    .describe('What the learner is told once they have committed.');
export const startAttemptSchema = z
    .object({ attemptId: attemptIdSchema })
    .describe('POST /lessons/:slug/attempts');
/** Active time in a run, without the gaps between sittings. Capped at the day a run stays resumable. */
const playedMsSchema = z.number().int().min(0).max(86_400_000);
/**
 * Where an interrupted run picks up — CONT-05.
 *
 * `resume` is absent whenever the lesson should open on its cover: no unfinished
 * attempt, nothing left to play, or a run left alone long enough that landing mid-lesson
 * would be confusing. The client draws the cover in all three cases, so it needs no rule
 * of its own to tell them apart.
 *
 * `finished` is independent of it — UI-103: a replay left halfway has both.
 */
export const lessonResumeSchema = z
    .object({
    finished: z
        .boolean()
        .describe('Whether this learner has completed the lesson before: the fact the finish reads as a replay.'),
    resume: z
        .object({
        attemptId: attemptIdSchema.describe('The unfinished attempt the run continues, never a new one.'),
        openAt: idSchema.describe('The furthest scene the learner opened, or the one after the furthest they answered if that is further.'),
        playedMs: playedMsSchema.describe('Time spent in the run before it was left, up to opening the furthest scene, so the finish can count every sitting.'),
        draft: sceneAnswerSchema
            .optional()
            .describe('The answer the learner had started on `openAt` and not submitted. Present only when one is kept.'),
    })
        .optional(),
})
    .describe('GET /lessons/:slug/resume');
/** The scene a run has reached, sent as each one opens: a narrative is never answered. */
export const reachSceneSchema = z
    .object({
    sceneId: idSchema,
    playedMs: playedMsSchema
        .optional()
        .describe('Time spent in the run so far, every sitting included. A running total rather than a step, so a retried request cannot count twice.'),
    draft: sceneAnswerSchema
        .optional()
        .describe('The answer started on this scene and not yet submitted. Kept only when the scene is the furthest one reached; ignored on a step back.'),
})
    .describe('PUT /attempts/:id/position');
export const attemptPositionSchema = z
    .object({
    sceneId: idSchema.describe('The furthest scene the run has opened. A step back never moves it.'),
})
    .describe('Where an attempt has got to.');
export const completeAttemptSchema = z
    .object({
    utcOffsetMinutes: utcOffsetMinutesSchema,
    collection: slugSchema
        .optional()
        .describe('The collection the lesson was opened from, so the next lesson offered is that collection’s. Ignored unless it holds the lesson.'),
})
    .describe('POST /attempts/:id/complete');
/**
 * What the completion screen shows, and where every number on it comes from — GEN-91.
 *
 * Derived here rather than generated into each lesson as a `REWARD` scene: a scene could
 * only ever carry a sentence somebody typed, so a lesson’s celebration named no XP the
 * learner earned and no streak they were on. `sceneAuthorable` is the other half.
 *
 * Only what actually happened: `streakExtended` is false on a second lesson the same day,
 * and `badgesEarned` is empty on almost every completion. A client that draws a row per
 * present field draws nothing it has to invent.
 */
export const completionRewardSchema = z
    .object({
    xpTotal: z
        .number()
        .int()
        .min(0)
        .describe('Every point the learner holds, this attempt included.'),
    streakDays: z
        .number()
        .int()
        .min(0)
        .describe('The run they are on now, in days.'),
    streakExtended: z
        .boolean()
        .describe('Whether this completion is what moved it.'),
    badgesEarned: badgeSchema
        .omit({ earned: true })
        .array()
        .describe('Badges this completion unlocked, in catalogue order.'),
})
    .describe('The universal post-lesson reward, from the learner’s own projections.');
/**
 * The milestone a finished path closes on — CONT-04.
 *
 * On the completion response rather than reached by a second read, because the client
 * cannot learn it any other way: `collection.completed` is an outcome, and
 * `on-fact.decorator.ts` says there is deliberately no `OnOutcome`, so the event the
 * projector emits never leaves the server. One request already knows.
 *
 * Present only on the completion that closed a `PATH`. A module or a course closing
 * leaves it absent, and so does a retry of a completion that already landed — the same
 * rule `reward` follows, and for the same reason: only what actually happened.
 */
export const pathCompleteSchema = z
    .object({
    slug: slugSchema,
    title: z.string().min(1).max(120),
    /**
     * What the learner can now do. The path's own summary rather than generated
     * prose, on CONT-01's rule for the recommendation beside it.
     */
    takeaway: z.string().min(1).max(300),
    lessonsDone: z.number().int().min(1),
    /** XP earned across the path, not on this attempt. */
    xp: z.number().int().min(0),
    /**
     * The one path offered next — never a shortlist. Absent when nothing published is
     * untouched, and the screen still resolves on the catalogue alone.
     */
    next: homeRecommendationSchema.optional(),
})
    .describe('What finishing a path returns, on the completion that closed it.');
/**
 * The quieter two rungs, on the completion that closed one — CONT-07.
 *
 * A union rather than a kind beside optional fields because the sizing is the risk the
 * card names: a module is a mark and may not carry a line, a course is a mark and
 * exactly one line of what the learner can now do. `PATH` is absent by construction —
 * that rung is `pathComplete`, which earns a screen where these two earn a row.
 *
 * It rides the completion response for the reason `pathComplete` does: the event the
 * projector emits never leaves the server, and one request already knows.
 */
export const collectionClosedSchema = z
    .discriminatedUnion('kind', [
    z.object({
        kind: z.literal('MODULE'),
        slug: slugSchema,
        title: z.string().min(1).max(120),
    }),
    z.object({
        kind: z.literal('COURSE'),
        slug: slugSchema,
        title: z.string().min(1).max(120),
        /**
         * The course's own summary, on CONT-01's rule against generated prose —
         * trimmed, and **optional**: a course with nothing to say still closed, and the
         * mark is what says so. Dropping the whole mark instead left the response
         * claiming nothing closed while `collection.completed` said a course had.
         */
        can: z.string().trim().min(1).max(300).optional(),
    }),
])
    .describe('The module or course this completion closed, if it closed one.');
export const nextLessonSchema = z
    .object({
    slug: slugSchema,
    title: z.string().min(1),
})
    .describe('The lesson that follows this one in the learner’s journey — the same one Home would name.');
export const attemptResultSchema = z
    .object({
    attemptId: attemptIdSchema,
    scorePct: z.number().int().min(0).max(100),
    xpAwarded: z.number().int().min(0),
    localDate: z.iso
        .date()
        .describe('The learner’s calendar date, YYYY-MM-DD.'),
    tally: lessonScoreSchema.describe('This attempt’s graded scenes and how many were fully right — what `scorePct` is taken from.'),
    reward: completionRewardSchema,
    pathComplete: pathCompleteSchema.optional(),
    closed: collectionClosedSchema
        .optional()
        .describe('Absent unless this completion closed a module or a course.'),
    next: nextLessonSchema
        .optional()
        .describe('Absent when the journey has nothing left to open.'),
    goalMet: z
        .boolean()
        .describe('The learner’s daily target is met on their local date, counting this lesson.'),
    goalReached: z
        .boolean()
        .describe('This lesson is the one that met the daily target: false on every lesson after it that day, and on a replay of one already counted.'),
    replay: z
        .boolean()
        .describe('The learner had finished this lesson before this attempt, so the lesson end can say it is a replay. A replay is paid the accuracy bonus without the finishing floor.'),
})
    .describe('What completing a lesson returns.');
//# sourceMappingURL=progress.js.map